import React, { useState, useEffect, useCallback } from 'react';
import './App.css';

interface PhotoData {
  file: File;
  preview: string;
}

function App() {
  const [photo, setPhoto] = useState<PhotoData | null>(null);
  const [certificate, setCertificate] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const generateCertificate = useCallback(async () => {
    setIsGenerating(true);
    try {
      console.log('Generating certificate for photo:', photo);
      
      // Convert photo to base64
      const base64 = await fileToBase64(photo!.file);
      console.log('Base64 conversion successful, length:', base64.length);
      
      // Fetch the source SVG
      const response = await fetch('/source.svg');
      if (!response.ok) {
        throw new Error(`Failed to fetch SVG: ${response.status}`);
      }
      const svgContent = await response.text();
      console.log('SVG loaded successfully, length:', svgContent.length);
      
      // Replace the entire image tag with id="image_to_replace"
      // Based on SVG analysis: clip-path rectangle is at x="306.14" y="536.89" with width="497.01" height="637.6"
      // The clip-path has a rotation transform: matrix(0.9968 -0.0803 0.0803 0.9968 -66.8889 47.2769) ≈ 4.6 degrees
      // Apply the same transform to the image to match the slant
      const updatedSvg = svgContent.replace(
        /<image[^>]*id="image_to_replace"[\s\S]*?xlink:href="[^"]*"[\s\S]*?>/,
        `<image id="image_to_replace" style="overflow:visible;" x="306.14" y="536.89" width="497.01" height="637.6" preserveAspectRatio="xMidYMid slice" transform="matrix(0.9968 -0.0803 0.0803 0.9968 -66.8889 47.2769)" xlink:href="${base64}">`
      );
      
      console.log('SVG replacement completed');
      
      // Verify the replacement worked
      const newImageTag = updatedSvg.match(/<image[^>]*id="image_to_replace"[^>]*>/);
      if (newImageTag) {
        console.log('New image tag:', newImageTag[0]);
      } else {
        console.log('Image tag not found after replacement!');
      }
      
      console.log('SVG replacement successful');
      console.log('Updated SVG preview (first 500 chars):', updatedSvg.substring(0, 500));
      console.log('Certificate state will be set to:', updatedSvg.length, 'characters');
      
      // Check if the base64 image was actually replaced
      const imageMatch = updatedSvg.match(/xlink:href="([^"]*)"/);
      if (imageMatch) {
        console.log('Image href in SVG:', imageMatch[1].substring(0, 100) + '...');
        console.log('Expected base64:', base64.substring(0, 100) + '...');
        console.log('Base64 match:', imageMatch[1] === base64);
      }
      
      setCertificate(updatedSvg);
    } catch (error) {
      console.error('Error generating certificate:', error);
      alert('Error generating certificate. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [photo]);

  // Generate certificate when photo changes
  useEffect(() => {
    if (photo) {
      generateCertificate();
    } else {
      setCertificate(null);
    }
  }, [photo, generateCertificate]);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    console.log('File selected:', file);
    if (file && file.type.startsWith('image/')) {
      console.log('Valid image file, bypassing crop flow - using original image directly');
      // Skip cropping and use the original image directly
      const preview = URL.createObjectURL(file);
      setPhoto({ file: file, preview });
    } else {
      console.log('Invalid file type or no file selected');
    }
  };


  const handleDownload = () => {
    if (certificate) {
      // Create a canvas to convert SVG to PNG
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Set canvas size (you can adjust these dimensions)
      canvas.width = 1080; // Match SVG viewBox width
      canvas.height = 1921; // Match SVG viewBox height

      // Create an image from the SVG
      const img = new Image();
      img.onload = () => {
        // Draw the SVG image onto the canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Convert canvas to PNG blob
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'funx-certificate.png';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }
        }, 'image/png', 1.0);
      };
      
      // Convert SVG to data URL
      const svgBlob = new Blob([certificate], { type: 'image/svg+xml' });
      const svgUrl = URL.createObjectURL(svgBlob);
      img.src = svgUrl;
    }
  };

  return (
    <div className="app">
      
      <main className="main">
        <div className="upload-section">
          <div className="upload-header">
            <img src="/funmiyo.webp" alt="Funmiyo Logo" className="header-logo" />
            <h1><div>FUN X CHAMPS</div><div>Certificate Generator</div></h1>
          </div>
          {!photo ? (
            <div className="upload-area">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                id="photo-upload"
                style={{ display: 'none' }}
              />
              <label htmlFor="photo-upload" className="upload-label">
                <div className="upload-icon">📷</div>
                <p>Click to upload photo</p>
                <p className="upload-hint">PNG, JPG, JPEG, WEBP</p>
              </label>
            </div>
          ) : (
            <div className="photo-preview">
              <img src={photo!.preview} alt="Preview" />
              <div className="photo-actions">
                <button onClick={() => setPhoto(null)}>Remove</button>
                <label htmlFor="photo-upload" className="change-photo">
                  Change Photo
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  id="photo-upload"
                  style={{ display: 'none' }}
                />
              </div>
              <button 
                className="download-btn" 
                onClick={handleDownload}
                disabled={isGenerating}
              >
                {isGenerating ? 'Generating...' : 'Download Certificate'}
              </button>
            </div>
          )}
        </div>

        <div className="preview-section">
          <div className="certificate-container">
            {isGenerating ? (
              <div className="loading">
                <div className="spinner"></div>
                <p>Generating certificate...</p>
              </div>
            ) : certificate ? (
              <div 
                className="certificate"
                dangerouslySetInnerHTML={{ __html: certificate }}
              />
            ) : (
              <div className="empty-state">
                <p>Upload a photo to see your certificate preview</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;